import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import React from 'react';

import { validateEmail } from '@hotjar/webapp/lib/email';
import { Select } from '@hotjar/webapp/components/Select';
import type { Organization, OrganizationAccess } from '@hotjar/organization';
import { ACCESS_CHOICES } from '@hotjar/webapp/constants/accessChoices';
import { tokens, Icon, EmailInput, Tooltip } from '@hotjar/hotsauce';
import { useHelpHotjarUrl } from '@hotjar/translations';

import { S } from './Styles';

const USER_PERMISSION_ARTICLE_LINK = '/articles/115011734788-User-Permissions';

export type InvitationWithRowId = {
  rowId: number;
  email: string;
  access: null | OrganizationAccess;
};

export interface InvitationFormProps {
  organization: Organization;
  invitations: InvitationWithRowId[];
  onEmailChange: (index: number, value: string) => void;
  onAccessChange: (index: number, value: OrganizationAccess) => void;
}

export const InvitationForm = ({
  organization,
  invitations,
  onEmailChange,
  onAccessChange,
}: InvitationFormProps): JSX.Element => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();

  return (
    <div>
      <h2
        css={css`
          margin-bottom: ${tokens.spacer.x3};
        `}
      >
        {t('common:team-invite.message-all-people-access')} {organization.name}.
      </h2>

      <S.Grid rows={1}>
        <S.GridCellHeader>{t('common:team-invite.team-member')}</S.GridCellHeader>
        <S.GridCellHeader className="access">
          {t('common:team-invite.access-level')}{' '}
          <Tooltip text={t('common:team-invite.learn-about-access')}>
            <a
              href={`${helpHotjarUrl}${USER_PERMISSION_ARTICLE_LINK}`}
              target="_blank"
              rel="noreferrer"
            >
              <Icon icon="info" inline />
            </a>
          </Tooltip>
        </S.GridCellHeader>
      </S.Grid>

      <S.Grid aria-label="invitation list" role="grid" rows={invitations.length}>
        {invitations.map((emailRow, index) => (
          <React.Fragment key={emailRow.rowId}>
            <EmailInput
              name={`email${index}`}
              placeholder="Email address"
              icon="user_avatar"
              value={emailRow.email}
              isInvalid={Boolean(emailRow.email) && !validateEmail(emailRow.email)}
              onChange={(ev) => onEmailChange(index, ev.target.value)}
            />
            <Select
              aria-label="user permission"
              name={`access${index}`}
              value={emailRow.access ?? undefined}
              onChange={(ev) =>
                onAccessChange(index, (ev.target as HTMLSelectElement).value as OrganizationAccess)
              }
            >
              <option value="">{t('common:team-invite.select-access-placeholder')}</option>
              {ACCESS_CHOICES.map((access) => (
                <option key={access.value} value={access.value}>
                  {access.label}
                </option>
              ))}
            </Select>
          </React.Fragment>
        ))}
      </S.Grid>
    </div>
  );
};
